import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { listShippingServices } from '../../plans/services/PlanCRUD';
import { addCoupon } from '../../user-profile/services/UserCRUD';
import { DraftState } from '../hooks/useDraft';
import {
  QuickSaleAddressModel,
  QuickSalePlanModel,
} from '../models/QuickSaleModel';
import { apiErrorMessage, formatEuro } from '../utils/quickSaleFormat';
import { formatAddress } from './CustomerCard';
import { FieldRow, QuickSaleCard } from './QuickSaleCard';

type Props = {
  userId: number;
  plan: QuickSalePlanModel | null;
  savedPlan: QuickSalePlanModel | null;
  addresses: QuickSaleAddressModel[];
  address: QuickSaleAddressModel | null;
  draftState: DraftState;
  onCouponApplied: () => Promise<void> | void;
};

/** Where and how the plan is delivered, what it costs, and the coupon. */
const ShippingCard: FC<Props> = ({
  userId,
  plan,
  savedPlan,
  addresses,
  address,
  draftState,
  onCouponApplied,
}) => {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  const [carriers, setCarriers] = useState<Array<{ id: number; name: string }>>(
    []
  );
  const [coupon, setCoupon] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const { draft, update, clear } = draftState;
  const carrierId =
    (draft.address?.id === address?.id && draft.address?.shipping_service) ||
    address?.shipping_service?.id;

  useEffect(() => {
    if (!editing) {
      return;
    }
    listShippingServices()
      .then(({ data }) => setCarriers(data))
      .catch((error) => console.warn(error));
  }, [editing]);

  function onAddressChange(addressId: number) {
    const saved = savedPlan?.wallet_shipping_address_id;
    update('plan', {
      wallet_shipping_address: addressId === saved ? undefined : addressId,
    });
    // A carrier picked for the previous address does not follow the plan.
    if (draft.address && draft.address.id !== addressId) {
      clear('address');
    }
  }

  function onCarrierChange(serviceId: number) {
    if (!address) {
      return;
    }
    update('address', {
      id: address.id,
      shipping_service:
        serviceId === address.shipping_service?.id ? undefined : serviceId,
    });
  }

  async function onApplyCoupon(event: React.FormEvent) {
    event.preventDefault();
    const code = coupon.trim();
    if (!code) {
      return;
    }
    setApplyingCoupon(true);
    try {
      await addCoupon(code, userId);
      toast.success(
        intl.formatMessage({ id: 'quickSale.couponApplied' }, { code })
      );
      setCoupon('');
      await onCouponApplied();
    } catch (error) {
      toast.error(
        apiErrorMessage(
          error,
          intl.formatMessage({ id: 'quickSale.couponError' })
        )
      );
    } finally {
      setApplyingCoupon(false);
    }
  }

  const shippingFee = plan?.shipping_fee;

  return (
    <QuickSaleCard
      icon="fa-truck"
      title={intl.formatMessage({ id: 'quickSale.shippingTitle' })}
      editing={editing}
      onToggleEdit={() => setEditing(!editing)}
      className="h-100"
    >
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.deliveryAddress' })}
        changed={draft.plan?.wallet_shipping_address !== undefined}
      >
        {editing ? (
          <select
            className="form-select form-select-sm"
            value={address?.id ?? ''}
            aria-label={intl.formatMessage({ id: 'quickSale.deliveryAddress' })}
            onChange={(event) => onAddressChange(Number(event.target.value))}
          >
            {addresses.map((option) => (
              <option key={option.id} value={option.id}>
                {formatAddress(option)}
              </option>
            ))}
          </select>
        ) : (
          formatAddress(address)
        )}
      </FieldRow>
      <FieldRow
        label={intl.formatMessage({ id: 'quickSale.carrier' })}
        changed={draft.address?.shipping_service !== undefined}
      >
        {editing ? (
          <>
            <select
              className="form-select form-select-sm"
              value={carrierId ?? ''}
              aria-label={intl.formatMessage({ id: 'quickSale.carrier' })}
              onChange={(event) => onCarrierChange(Number(event.target.value))}
            >
              {!carrierId && <option value="">-</option>}
              {carriers.map((carrier) => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </option>
              ))}
            </select>
            <div className="fs-8 text-muted fw-normal mt-1">
              {intl.formatMessage({ id: 'quickSale.carrierScope' })}
            </div>
          </>
        ) : (
          carriers.find((carrier) => carrier.id === carrierId)?.name ||
          address?.shipping_service?.name ||
          '-'
        )}
      </FieldRow>
      <FieldRow label={intl.formatMessage({ id: 'quickSale.shippingCosts' })}>
        {shippingFee === 0
          ? intl.formatMessage({ id: 'quickSale.free' })
          : formatEuro(intl, shippingFee)}
      </FieldRow>

      <h4 className="fw-bolder mt-6 mb-3">
        <i className="fas fa-tag me-2 text-gray-800" />
        {intl.formatMessage({ id: 'quickSale.coupon' })}
      </h4>
      <form className="d-flex gap-2" onSubmit={onApplyCoupon}>
        <input
          className="form-control"
          value={coupon}
          placeholder={intl.formatMessage({ id: 'quickSale.couponCode' })}
          onChange={(event) => setCoupon(event.target.value)}
        />
        <button
          type="submit"
          className="btn btn-light-primary"
          disabled={!coupon.trim() || applyingCoupon}
        >
          {applyingCoupon && (
            <span className="spinner-border spinner-border-sm me-2" />
          )}
          {intl.formatMessage({ id: 'quickSale.apply' })}
        </button>
      </form>
    </QuickSaleCard>
  );
};

export { ShippingCard };
